import { createPortal } from 'react-dom';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

import JxForm from '../JxForms/JxForm';
import JxTitleBar from '../JxTitleBar/JxTitleBar';
import JxButton from '../JxButtons/JxButton';
import JxLineEdit from '../JxEdits/JxLineEdit';

import { TITLE_BAR_ACTIONS } from './JxStopwatchTitleBarActions';

import '../JxTheme.css';
import './JxStopwatchForm.css';

const TICK_MS = 37;

/**
 * Formats elapsed milliseconds as hh:mm:ss.cc
 * @param {number} ms
 */
const formatElapsed = (ms) => {
    const cs = Math.floor((ms % 1000) / 10);
    const totalSeconds = Math.floor(ms / 1000);
    const s = totalSeconds % 60;
    const m = Math.floor(totalSeconds / 60) % 60;
    const h = Math.floor(totalSeconds / 3600);
    const pad = (n, len = 2) => String(n).padStart(len, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(cs)}`;
};

/**
 * JxStopwatchForm — digital stopwatch, JxForm-based scaffold, same shape as JxClockTestForm.
 * Multi-instance via JxWindowManager (openWindow assigns unique id — no dedicated State class needed).
 * @param {string} description - initial description text, editable via JxLineEdit
 */
export default class JxStopwatchForm extends JxForm {
    constructor(props) {
        super(props);
        this.name = 'JxStopwatchForm';
        this.schema = {};
        this.state = { ...this.state, elapsedMs: 0, running: false, description: props.description ?? '' };
        this._startTimestamp = null;
        this._tickId = null;
    }

    // Literal, hardcoded default — no window.innerWidth/innerHeight centering math.
    getDefaultGeo = () => ({ top: 300, left: 600, width: 220, height: 220 });

    componentDidMount() {
        super.componentDidMount();
        setTimeout(() => this.bringToTop(), 100);
        if (this.props.autoStart) this.handleStart();
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        clearInterval(this._tickId);
    }

    handleStart = () => {
        if (this.state.running) return;
        this._startTimestamp = Date.now() - this.state.elapsedMs;
        this._tickId = setInterval(() => this.setState({ elapsedMs: Date.now() - this._startTimestamp }), TICK_MS);
        this.setState({ running: true });
    };

    handlePause = () => {
        clearInterval(this._tickId);
        this.setState({ running: false });
    };

    handleReset = () => {
        clearInterval(this._tickId);
        this._startTimestamp = null;
        this.setState({ elapsedMs: 0, running: false });
    };

    handleDescriptionChange = (value) => {
        this.setState({ description: value });
    };

    getTitleBarActions() {
        return [
            {
                icon: this.state.isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />,
                title: this.state.isPinned ? 'Unpin' : 'Pin on top',
                onClick: this.togglePin,
            },
            ...TITLE_BAR_ACTIONS(this.props.onClose),
        ];
    }

    render() {
        if (!this.props.open) return null;

        const { elapsedMs, running, description } = this.state;

        return createPortal(
            <div
                {...this.panelProps}
                style={this.panelStyle}
                className={`jx-stopwatch-window ${this.panelActiveClass} ${this.panelPinnedClass}`}
            >
                <JxTitleBar
                    id={`${this.name}_${this.props.id}_titlebar`}
                    title="Stopwatch"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={this.getTitleBarActions()}
                />

                <div className="jx-stopwatch-window-body">
                    <span className="jx-stopwatch-window-digital">{formatElapsed(elapsedMs)}</span>

                    <JxLineEdit
                        id={`${this.name}_${this.props.id}_description`}
                        value={description}
                        onChange={this.handleDescriptionChange}
                        placeholder="Description…"
                        maxLength={80}
                    />

                    <div className="jx-stopwatch-window-actions">
                        {running ? (
                            <JxButton label="Pause" variant="secondary" onClick={this.handlePause} />
                        ) : (
                            <JxButton label="Start" variant="primary" onClick={this.handleStart} />
                        )}
                        <JxButton label="Reset" variant="secondary" onClick={this.handleReset} />
                    </div>
                </div>
            </div>,
            document.body,
        );
    }
}
